import { PermissionScope, type Permission, type PersonnelTmdPermission } from "src/api/Tmd.dtos";

/**
 * Evaluates a permission against the existing client-side trial/institution scope rules.
 *
 * Postconditions: Both IDs permit a matching trial-institution or trial grant; trial alone also
 * accepts matching trial-institution grants. Institution alone checks institution scope. Global
 * grants are always considered; zero counts as a supplied ID. Missing grants return false. This
 * UI decision does not authorize server operations.
 */
const hasPermission = (
    personnelTmdPermissions: PersonnelTmdPermission[],
    permission: Permission,
    trialId?: number,
    institutionId?: number
): boolean => {
    // Check the user has any permissions at all.
    if (personnelTmdPermissions && personnelTmdPermissions.length > 0) {
        // Check for permission on Trial Institution.
        if (
            (trialId || trialId === 0) && // has trial ID
            (institutionId || institutionId === 0) // has institution ID
        ) {
            if (
                personnelTmdPermissions.some(g =>
                    g.scope === PermissionScope.TrialInstitution &&
                    g.trialId == trialId &&
                    g.institutionId == institutionId &&
                    g.permission == permission
                )
            ) {
                return true;
            }

            // A trial grant also covers the requested trial/institution pair.
            if (
                personnelTmdPermissions.some(g =>
                    g.scope === PermissionScope.Trial &&
                    g.trialId == trialId &&
                    g.permission == permission
                )
            ) {
                return true;
            }
        }

        // Check for permission on Trial
        if (
            (trialId || trialId === 0) && // has trialId
            !institutionId &&
            institutionId !== 0 // does not have institution ID
        ) {
            if (
                personnelTmdPermissions.some(g =>
                    (g.scope === PermissionScope.Trial ||
                        g.scope === PermissionScope.TrialInstitution) &&
                    g.trialId == trialId &&
                    g.permission == permission
                )
            ) {
                return true;
            }
        }

        // Check for permission on Institution
        if (
            !trialId &&
            trialId !== 0 && // does not have trial ID
            (institutionId || institutionId === 0) // has institution ID
        ) {
            if (
                personnelTmdPermissions.some(g =>
                    g.scope === PermissionScope.Institution &&
                    g.institutionId == institutionId &&
                    g.permission == permission
                )
            ) {
                return true;
            }
        }

        // Check for global permission
        if (
            personnelTmdPermissions.some(g =>
                g.scope === PermissionScope.Global && g.permission == permission
            )
        ) {
            return true;
        }
    }

    return false;
};

export default hasPermission;
